import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { AssetRegistry } from '../systems/AssetRegistry';
import { DataRegistry } from '../systems/DataRegistry';
import { startNewCampaign } from '../systems/GameBootstrap';
import { AudioManager } from '../systems/AudioManager';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { drawPanel } from '../ui/components/SceneChrome';

export class MainMenuScene extends Phaser.Scene {
  private factionIndex = 0;
  private scenarioIndex = 0;

  constructor() {
    super(SceneKeys.MainMenu);
  }

  create() {
    AudioManager.instance.playMusic(this, 'menu_main');
    this.render();
  }

  private render() {
    this.children.removeAll();
    const { width, height } = this.scale;
    const registry = DataRegistry.instance;
    const factions = registry.factionList();
    const scenarios = registry.scenarioList();
    const scenario = scenarios[this.scenarioIndex] ?? scenarios[0];
    const faction = factions[this.factionIndex] ?? factions[0];

    const background = AssetRegistry.instance.image('main_menu_background', 'fallback.title');
    this.add.image(width / 2, height / 2, this.textures.exists(background) ? background : 'fallback.title')
      .setDisplaySize(width, height)
      .setAlpha(0.86);
    this.add.rectangle(width / 2, height / 2, width, height, VisualTheme.colors.ink, 0.38);

    this.add.rectangle(width / 2, 64, width, 128, VisualTheme.colors.night, 0.94)
      .setStrokeStyle(2, VisualTheme.colors.gold, 0.92);
    this.add.rectangle(width / 2, 122, width - 30, 2, VisualTheme.colors.goldBright, 0.36);
    this.add.text(38, 24, 'Start Campaign', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '42px',
      color: VisualTheme.text.title,
      stroke: '#160e08',
      strokeThickness: 2,
    });
    this.add.text(40, 78, 'Choose a historical scenario and ruling faction.', {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '17px',
      color: VisualTheme.text.accent,
    });

    drawPanel(this, 380, 470, 620, 610, 'Historical Scenario');
    this.add.text(100, 232, scenario.name, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '25px',
      color: VisualTheme.text.accent,
      wordWrap: { width: 520 },
    });
    this.add.text(100, 286, `Start date: ${scenario.month}/${scenario.year}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '17px',
      color: VisualTheme.text.body,
    });
    this.add.text(100, 316, `Recommended: ${registry.factions[scenario.recommendedFaction]?.name ?? scenario.recommendedFaction}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '17px',
      color: VisualTheme.text.body,
    });
    this.add.text(100, 360, 'Strategic focus', {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '21px',
      color: VisualTheme.text.title,
    });
    this.add.text(100, 394, this.scenarioSummary(scenario.id), {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '16px',
      color: VisualTheme.text.body,
      wordWrap: { width: 520 },
      lineSpacing: 5,
    });
    createTextButton(this, {
      label: '< Scenario',
      x: 230,
      y: 710,
      width: 230,
      onClick: () => {
        this.scenarioIndex = (this.scenarioIndex - 1 + scenarios.length) % scenarios.length;
        this.render();
      },
    });
    createTextButton(this, {
      label: 'Scenario >',
      x: 530,
      y: 710,
      width: 230,
      onClick: () => {
        this.scenarioIndex = (this.scenarioIndex + 1) % scenarios.length;
        this.render();
      },
    });

    drawPanel(this, 1090, 470, 760, 610, 'Ruling Faction');
    const factionColor = Phaser.Display.Color.HexStringToColor(faction.colors.primary).color;
    this.add.rectangle(1090, 183, 716, 10, factionColor, 0.95);

    const emblem = AssetRegistry.instance.image(faction.emblemAsset, 'fallback.banner');
    this.add.image(805, 250, this.textures.exists(emblem) ? emblem : 'fallback.banner')
      .setDisplaySize(112, 128);
    this.add.text(885, 202, faction.name, {
      fontFamily: VisualTheme.fonts.display,
      fontSize: '34px',
      color: faction.colors.accent,
      stroke: '#160e08',
      strokeThickness: 2,
    });
    this.add.text(885, 246, `${faction.primaryCulture}  •  ${faction.stateReligion}  •  ${faction.aiPersonality.replaceAll('_', ' ')}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '16px',
      color: VisualTheme.text.accent,
    });
    this.add.text(770, 330, faction.longDescription, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '17px',
      color: VisualTheme.text.body,
      wordWrap: { width: 620 },
      lineSpacing: 5,
    });
    this.add.text(770, 450, `Start provinces: ${faction.startProvinces.map((id) => registry.provinces[id]?.name ?? id).join(', ') || 'Scenario dependent'}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '16px',
      color: VisualTheme.text.body,
      wordWrap: { width: 620 },
    });
    this.add.text(770, 506, `Roster: ${faction.roster.slice(0, 12).map((id) => registry.units[id]?.name ?? id).join(', ')}`, {
      fontFamily: VisualTheme.fonts.body,
      fontSize: '15px',
      color: VisualTheme.text.accent,
      wordWrap: { width: 620 },
    });
    createTextButton(this, {
      label: '< Faction',
      x: 940,
      y: 710,
      width: 220,
      onClick: () => {
        this.factionIndex = (this.factionIndex - 1 + factions.length) % factions.length;
        this.render();
      },
    });
    createTextButton(this, {
      label: 'Faction >',
      x: 1250,
      y: 710,
      width: 220,
      onClick: () => {
        this.factionIndex = (this.factionIndex + 1) % factions.length;
        this.render();
      },
    });

    createTextButton(this, {
      label: 'Begin Campaign',
      x: width / 2,
      y: height - 66,
      width: 320,
      onClick: () => {
        startNewCampaign(scenario.id, faction.id);
        this.scene.start(SceneKeys.CampaignMap);
      },
    });
    createTextButton(this, {
      label: 'Back',
      x: 120,
      y: height - 66,
      width: 170,
      onClick: () => this.scene.start(SceneKeys.Title),
    });

    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => JSON.stringify({ mode: 'setup', scenario: scenario.id, faction: faction.id, scenarios: scenarios.length, factions: factions.length });
    exposed.advanceTime = () => undefined;
  }

  private scenarioSummary(id: string) {
    const summaries: Record<string, string> = {
      '1293_majapahit': 'Imperial consolidation after the rise of Majapahit. Build legitimacy, secure rice stores, and expand across Java.',
      '1478_fracture': 'The old order fractures. Religious change, coastal sultanates, and rival courts compete for legitimacy.',
      '1511_malacca_falls': 'Portuguese disruption reshapes port politics and trade concessions across the straits.',
      '1595_foreign_fleets': 'Gunpowder and foreign companies enter the balance of power. Trade and naval pressure intensify.',
      '1628_contest_for_java': 'Mataram expands inland while port powers resist and the VOC looks for footholds.',
      '1700_company_power': 'Company power dominates late-game survival. Hold sovereignty against colonial pressure.',
    };
    return summaries[id] ?? 'A historical start built from the campaign data files.';
  }
}
